import React, { useEffect, useState } from 'react';
import { Card, Table, Tag, message, Button, Popconfirm } from 'antd';
import dayjs from 'dayjs';
import api from '../../api/axios';
import useAuthStore from '../../store/authStore';

const statusLabels = {
  pending:   { label: 'Bekliyor',  color: 'orange' },
  scheduled: { label: 'Planlandı', color: 'blue' },
  completed: { label: 'Kullanıldı', color: 'green' },
  // Planlandı ama öğrenci telafi dersine gelmedi. Hak burada KAPANIR; öğrenci
  // hak kaybetmez, o devamsızlık için ayrı yeni bir hak açılır (backend:
  // AttendanceChargeService). Ayrı durum olmasının sebebi geçmişin doğru
  // anlatılması — "kaç telafi planlandı, kaçına gelinmedi" sorusu.
  no_show:   { label: 'Gelmedi',   color: 'volcano' },
  cancelled: { label: 'İptal',     color: 'red' },
};

const MANAGER_ROLES = ['developer_admin', 'super_admin', 'branch_manager'];

// Hak kapandıktan sonra iz taşıyan durumlar — "Kullanım Bilgisi" kolonu
// bunlarda dolu gelir.
const CLOSED_STATUSES = ['completed', 'no_show'];

const StudentMakeupTab = ({ studentId }) => {
  const { user } = useAuthStore();
  const [makeupRights, setMakeupRights] = useState([]);
  const [loading, setLoading] = useState(false);
  const [completing, setCompleting] = useState(null);

  useEffect(() => {
    fetchMakeupRights();
  }, [studentId]);

  const fetchMakeupRights = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/students/${studentId}/makeup-rights`);
      setMakeupRights(res.data || []);
    } catch (error) {
      message.error('Telafi hakları yüklenemedi.');
    } finally {
      setLoading(false);
    }
  };

  // "Bekleyen" YALNIZCA 'pending' demek — planlanmış hak bir seansa ayrılmış
  // durumdadır ve yeniden kullanılamaz. Eskiden planlanan hak hiçbir sayaca
  // girmiyordu (ne bekleyen ne kullanılan) ve ekranda kaybolduğu için
  // hakkından fazla telafi verilebiliyordu.
  const pending   = makeupRights.filter(m => m.status === 'pending').length;
  const scheduled = makeupRights.filter(m => m.status === 'scheduled').length;
  const used      = makeupRights.filter(m => m.status === 'completed').length;
  const noShow    = makeupRights.filter(m => m.status === 'no_show').length;

  const handleComplete = async (id) => {
    setCompleting(id);
    try {
      await api.patch(`/makeup-rights/${id}`, { status: 'completed' });
      message.success('Telafi hakkı tamamlandı olarak işaretlendi.');
      fetchMakeupRights();
    } catch {
      message.error('İşlem başarısız.');
    } finally {
      setCompleting(null);
    }
  };

  const isManager = MANAGER_ROLES.includes(user?.role);

  const columns = [
    {
      title: 'Devamsızlık Tarihi',
      key: 'date',
      render: (_, r) => dayjs(r.attendance?.lesson_session?.date).format('DD.MM.YYYY'),
    },
    {
      title: 'Ders Saati',
      key: 'time',
      render: (_, r) => `${r.attendance?.lesson_session?.start_time?.substring(0, 5)} — ${r.attendance?.lesson_session?.end_time?.substring(0, 5)}`,
    },

    {
      title: 'Durum',
      dataIndex: 'status',
      key: 'status',
      render: (v) => (
        <Tag color={statusLabels[v]?.color || 'default'}>
          {statusLabels[v]?.label || v}
        </Tag>
      ),
    },
    {
      // Sadece tarih yazmak yetmiyordu: ayni gun icinde farkli saat ve
      // derslikte birden fazla telafi seansi olabiliyor, hangisinin
      // kullanildigi anlasilmiyordu.
      title: 'Planlanan / Kullanılan Ders',
      key: 'scheduled',
      render: (_, r) => {
        const s = r.scheduled_session;
        if (!s) return <span className="text-gray-400">—</span>;
        return (
          <div>
            <div>{dayjs(s.date).format('DD.MM.YYYY')} {dayjs(s.date).format('dddd')}</div>
            <div className="text-xs text-gray-500">
              {s.start_time?.substring(0, 5)} — {s.end_time?.substring(0, 5)}
              {s.classroom?.name ? ` · ${s.classroom.name}` : ''}
            </div>
          </div>
        );
      },
    },
    {
      // "Kullanildi" yaziyordu ama kimin nasil kullandirdigi hicbir yerde
      // gorunmuyordu; elle kapatilan hak ile ogrencinin gercekten derse
      // gelerek kullandigi hak ayirt edilemiyordu.
      title: 'Kullanım Bilgisi',
      key: 'completion',
      render: (_, r) => {
        if (!CLOSED_STATUSES.includes(r.status)) return <span className="text-gray-400">—</span>;

        // Bu alanlar 05.08.2026'da eklendi; daha once kapatilmis haklarda
        // iz yok ve geriye donuk uretilemez.
        if (!r.completed_at && !r.completer) {
          return <span className="text-xs text-gray-400">Eski kayıt — iz tutulmamış</span>;
        }

        const isManual = r.completion_source === 'manual';
        const isNoShow = r.status === 'no_show';
        return (
          <div>
            <Tag color={isNoShow ? 'volcano' : (isManual ? 'purple' : 'cyan')} style={{ marginInlineEnd: 0 }}>
              {isNoShow
                ? (isManual ? 'Gelmedi — elle' : 'Gelmedi — yoklamadan')
                : (isManual ? 'Elle işaretlendi' : 'Yoklamadan')}
            </Tag>
            <div className="text-xs text-gray-500 mt-1">
              {r.completer ? `${r.completer.name} ${r.completer.surname}` : 'Bilinmiyor'}
              {r.completed_at ? ` · ${dayjs(r.completed_at).format('DD.MM.YYYY HH:mm')}` : ''}
            </div>
          </div>
        );
      },
    },
    ...(isManager ? [{
      title: 'İşlem',
      key: 'action',
      // Planlanmis hakta da gorunmeli: ogrenci telafiye geldigi halde yoklama
      // girilmediyse hakki kapatmanin baska yolu yoktu ve hak sonsuza kadar
      // "Planlandi" kalıyordu.
      render: (_, r) => ['pending', 'scheduled'].includes(r.status) ? (
        <Popconfirm
          title="Bu telafi hakkını tamamlandı olarak işaretle?"
          onConfirm={() => handleComplete(r.id)}
          okText="Evet"
          cancelText="Hayır"
        >
          <Button size="small" type="primary" loading={completing === r.id}>
            Manuel Tamamla
          </Button>
        </Popconfirm>
      ) : null,
    }] : []),
  ];

  return (
    <div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
        <Card size="small" className="text-center">
          <div className="text-2xl font-bold text-orange-500">{pending}</div>
          <div className="text-gray-500">Bekleyen Telafi Hakkı</div>
        </Card>
        <Card size="small" className="text-center">
          <div className="text-2xl font-bold text-blue-500">{scheduled}</div>
          <div className="text-gray-500">Planlandı</div>
        </Card>
        <Card size="small" className="text-center">
          <div className="text-2xl font-bold text-green-500">{used}</div>
          <div className="text-gray-500">Kullanılan Telafi</div>
        </Card>
        <Card size="small" className="text-center">
          <div className="text-2xl font-bold text-red-500">{noShow}</div>
          <div className="text-gray-500">Gelmedi</div>
        </Card>
      </div>

      <Table
        columns={columns}
        dataSource={makeupRights}
        rowKey="id"
        loading={loading}
        pagination={false}
        locale={{ emptyText: 'Telafi hakkı yok.' }}
      />
    </div>
  );
};

export default StudentMakeupTab;
